import { useMemo, useState } from 'react';
import { FaExclamationTriangle, FaSpinner } from 'react-icons/fa';
import CardLayout from '../common/CardLayout';
import { Checkbox } from '../ui/checkbox';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import FormConclusaoTarefa from './FormConclusaoTarefa';
import { PERIODICIDADES, nomePeriodicidade, nomeTipoEquipamento } from './periodicidades';

const formatarHora = (data) =>
    new Date(data).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

const formatarData = (data) =>
    new Date(`${data}T00:00:00`).toLocaleDateString('pt-BR');

const hojeIso = () => new Date().toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' });

function DescricaoPeriodo({ tarefa }) {
    if (tarefa.atrasada) {
        return <span className="text-red-400 font-semibold">Venceu em {formatarData(tarefa.dataAviso)}</span>;
    }
    if (tarefa.periodicidade === 'Diaria') return null;
    if (tarefa.dataAviso === hojeIso()) return <span>Vence hoje</span>;
    return <span>Até {formatarData(tarefa.dataAviso)}</span>;
}

export default function TarefasDoDia({ tarefas, carregando, erroCarregamento, onTentarNovamente, onAlternar }) {
    const [filtroPeriodicidade, setFiltroPeriodicidade] = useState('');
    const [ocultarConcluidas, setOcultarConcluidas] = useState(false);
    const [emAndamento, setEmAndamento] = useState(() => new Set());
    const [confirmandoId, setConfirmandoId] = useState(null);

    const resolvida = (t) => t.concluida || t.ignorada;
    const adiada = (t) => !resolvida(t) && Boolean(t.dataReagendada) && t.dataReagendada > hojeIso();
    const ordem = (t) => (t.atrasada ? 0 : adiada(t) ? 2 : 1);
    const totalConcluidas = tarefas.filter((t) => t.concluida).length;
    const totalIgnoradas = tarefas.filter((t) => t.ignorada).length;
    const totalReagendadas = tarefas.filter(adiada).length;
    const totalResolvidas = totalConcluidas + totalIgnoradas;
    const totalAtrasadas = tarefas.filter((t) => t.atrasada).length;

    const grupos = useMemo(() => {
        const filtradas = tarefas.filter((t) =>
            (!filtroPeriodicidade || t.periodicidade === filtroPeriodicidade) &&
            (!ocultarConcluidas || (!resolvida(t) && !adiada(t))));

        const mapa = new Map();
        filtradas.forEach((tarefa) => {
            if (!mapa.has(tarefa.tabelaManutencaoId)) {
                mapa.set(tarefa.tabelaManutencaoId, { id: tarefa.tabelaManutencaoId, nome: tarefa.nomeTabela, tipoEquipamento: tarefa.tipoEquipamento, tarefas: [] });
            }
            mapa.get(tarefa.tabelaManutencaoId).tarefas.push(tarefa);
        });

        return [...mapa.values()].map((grupo) => ({
            ...grupo,
            tarefas: [...grupo.tarefas].sort((a, b) => ordem(a) - ordem(b)),
        }));
    }, [tarefas, filtroPeriodicidade, ocultarConcluidas]);

    const iniciarConclusao = (tarefa) => {
        setConfirmandoId(tarefa.id);
    };

    const cancelarConclusao = () => {
        setConfirmandoId(null);
    };

    const alternar = async (tarefa, acao, dados) => {
        if (emAndamento.has(tarefa.id)) return;
        setEmAndamento((atual) => new Set(atual).add(tarefa.id));
        try {
            const ok = await onAlternar(tarefa, acao, dados);
            if (ok && acao !== 'reabrir') cancelarConclusao();
        } finally {
            setEmAndamento((atual) => {
                const novo = new Set(atual);
                novo.delete(tarefa.id);
                return novo;
            });
        }
    };

    const percentual = tarefas.length === 0 ? 0 : Math.round((totalResolvidas / tarefas.length) * 100);

    return (
        <div className="p-2">
            <CardLayout>
                <div className="flex items-center justify-between gap-2 p-2 flex-wrap">
                    <div>
                        <h2 className="text-2xl font-semibold">Tarefas do dia</h2>
                        <p className="text-lg text-gray-400">
                            {totalConcluidas} de {tarefas.length} concluídas
                            {totalIgnoradas > 0 && ` · ${totalIgnoradas} ignorada${totalIgnoradas > 1 ? 's' : ''}`}
                            {totalReagendadas > 0 && ` · ${totalReagendadas} reagendada${totalReagendadas > 1 ? 's' : ''}`}
                            {totalAtrasadas > 0 && (
                                <span className="ml-2 text-red-400 font-semibold">
                                    · {totalAtrasadas} atrasada{totalAtrasadas > 1 ? 's' : ''}
                                </span>
                            )}
                        </p>
                    </div>
                    <div className="flex items-center gap-4 flex-wrap">
                        <select
                            value={filtroPeriodicidade}
                            onChange={(e) => setFiltroPeriodicidade(e.target.value)}
                            className="bg-gray-800 text-lg text-white border border-gray-700 rounded px-3 py-1.5 focus:outline-none focus:border-accent"
                        >
                            <option value="">Todas as periodicidades</option>
                            {PERIODICIDADES.map((p) => (
                                <option key={p.valor} value={p.valor}>{p.nome}</option>
                            ))}
                        </select>
                        <label className="flex items-center gap-1.5 text-lg cursor-pointer">
                            <Checkbox checked={ocultarConcluidas} onCheckedChange={(v) => setOcultarConcluidas(v === true)} />
                            Ocultar concluídas
                        </label>
                    </div>
                </div>

                {tarefas.length > 0 && (
                    <div className="mx-2 mb-3 h-2 rounded bg-gray-800 overflow-hidden">
                        <div className="h-full bg-green-500 transition-all" style={{ width: `${percentual}%` }} />
                    </div>
                )}

                {carregando && tarefas.length === 0 ? (
                    <div className="flex justify-center p-4 text-gray-400"><FaSpinner className="animate-spin" /></div>
                ) : erroCarregamento && tarefas.length === 0 ? (
                    <div className="flex items-center justify-center gap-2 p-4 text-lg text-gray-300">
                        Não foi possível carregar as tarefas.
                        <button type="button" onClick={onTentarNovamente} className="text-accent underline">Tentar novamente</button>
                    </div>
                ) : grupos.length === 0 ? (
                    <div className="flex items-center justify-center p-4 mb-2 text-lg text-gray-300 border border-gray-700 rounded-lg bg-darkaccent/40" role="status">
                        {tarefas.length === 0 ? 'Nenhuma tarefa de manutenção para hoje.' : 'Nenhuma tarefa para os filtros selecionados.'}
                    </div>
                ) : (
                    <Accordion type="multiple" className="px-2">
                        {grupos.map((grupo) => {
                            const pendentes = grupo.tarefas.filter((t) => !resolvida(t) && !adiada(t)).length;
                            const reagendadas = grupo.tarefas.filter(adiada).length;
                            const atrasadas = grupo.tarefas.filter((t) => t.atrasada).length;
                            return (
                                <AccordionItem key={grupo.id} value={`tarefas-${grupo.id}`} className="border-b border-gray-800/60">
                                    <AccordionTrigger>
                                        <div className="flex w-full items-center justify-between gap-2 pr-3 text-left">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="font-semibold">{grupo.nome}</span>
                                                {grupo.tipoEquipamento && (
                                                    <span className="text-base px-2 py-0.5 rounded bg-accent/30 text-white">
                                                        Todos os {nomeTipoEquipamento(grupo.tipoEquipamento).toLowerCase()}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-2">
                                                {atrasadas > 0 && (
                                                    <span className="flex items-center gap-1 text-base px-2 py-0.5 rounded bg-red-700/70">
                                                        <FaExclamationTriangle /> {atrasadas} atrasada{atrasadas > 1 ? 's' : ''}
                                                    </span>
                                                )}
                                                {reagendadas > 0 && (
                                                    <span className="text-base px-2 py-0.5 rounded bg-blue-900/60 text-blue-200">
                                                        {reagendadas} reagendada{reagendadas > 1 ? 's' : ''}
                                                    </span>
                                                )}
                                                {(pendentes > 0 || reagendadas === 0) && (
                                                    <span className={`text-base px-2 py-0.5 rounded ${pendentes === 0 ? 'bg-green-700/60' : 'bg-yellow-700/60'}`}>
                                                        {pendentes === 0 ? 'Tudo concluído' : `${pendentes} pendente${pendentes > 1 ? 's' : ''}`}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </AccordionTrigger>
                                    <AccordionContent>
                                        <ul className="flex flex-col gap-1">
                                            {grupo.tarefas.map((tarefa) => (
                                                <li
                                                    key={tarefa.id}
                                                    className={`flex items-start gap-3 py-2 px-2 rounded border-l-4 ${tarefa.atrasada
                                                        ? 'border-red-500 bg-red-900/30'
                                                        : 'border-transparent'} ${adiada(tarefa) && confirmandoId !== tarefa.id ? 'opacity-50' : ''}`}
                                                >
                                                    <Checkbox
                                                        className={`mt-1 h-5 w-5 ${tarefa.atrasada ? 'border-red-400' : ''}`}
                                                        checked={resolvida(tarefa) || confirmandoId === tarefa.id}
                                                        disabled={emAndamento.has(tarefa.id)}
                                                        onCheckedChange={() => (resolvida(tarefa)
                                                            ? alternar(tarefa, 'reabrir')
                                                            : confirmandoId === tarefa.id ? cancelarConclusao() : iniciarConclusao(tarefa))}
                                                        aria-label={resolvida(tarefa) ? 'Reabrir tarefa' : 'Concluir, reagendar ou ignorar tarefa'}
                                                    />
                                                    <div className="flex-1 min-w-0">
                                                        <p className={`text-lg whitespace-pre-wrap break-words ${resolvida(tarefa)
                                                            ? 'line-through text-gray-400'
                                                            : tarefa.atrasada ? 'text-red-300' : ''}`}
                                                        >
                                                            {tarefa.acao}
                                                        </p>
                                                        <div className="flex flex-wrap items-center gap-2 mt-1 text-base text-gray-400">
                                                            <span className="px-2 py-0.5 rounded bg-gray-800 text-gray-200">{nomePeriodicidade(tarefa.periodicidade)}</span>
                                                            <DescricaoPeriodo tarefa={tarefa} />
                                                            {tarefa.concluida && tarefa.dataConclusao && (
                                                                <span>
                                                                    Concluída às {formatarHora(tarefa.dataConclusao)}
                                                                    {tarefa.executadoPor ? ` por ${tarefa.executadoPor}` : ''}
                                                                </span>
                                                            )}
                                                            {tarefa.ignorada && tarefa.dataConclusao && (
                                                                <span className="px-2 py-0.5 rounded bg-gray-700 text-gray-200">
                                                                    Ignorada às {formatarHora(tarefa.dataConclusao)}
                                                                    {tarefa.executadoPor ? ` por ${tarefa.executadoPor}` : ''}
                                                                </span>
                                                            )}
                                                            {!resolvida(tarefa) && tarefa.dataReagendada && (
                                                                <span className="px-2 py-0.5 rounded bg-blue-900/60 text-blue-200">
                                                                    Reagendada para {formatarData(tarefa.dataReagendada)}
                                                                </span>
                                                            )}
                                                        </div>
                                                        {resolvida(tarefa) && tarefa.observacao && (
                                                            <p className="mt-1 text-base text-gray-300 whitespace-pre-wrap break-words">
                                                                {tarefa.ignorada ? 'Motivo' : 'Observação'}: {tarefa.observacao}
                                                            </p>
                                                        )}
                                                        {confirmandoId === tarefa.id && !resolvida(tarefa) && (
                                                            <FormConclusaoTarefa
                                                                tarefa={tarefa}
                                                                enviando={emAndamento.has(tarefa.id)}
                                                                onConfirmar={(acao, dados) => alternar(tarefa, acao, dados)}
                                                                onCancelar={cancelarConclusao}
                                                            />
                                                        )}
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>
                                    </AccordionContent>
                                </AccordionItem>
                            );
                        })}
                    </Accordion>
                )}
            </CardLayout>
        </div>
    );
}
